// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.ReportViewerFilters.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observer } from 'mobx-react';
import React, { Component, PropTypes } from 'react';

import Panel from 'share/components/controls/Izenda.Panel';
import constants from 'logic/data/Izenda.ReportsConstants';
import FilterItem from 'modules/Reports/FilterProperty/Izenda.FilterItem';
import connect from 'share/enhancers/Izenda.Connect';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import CrossFiltering from 'modules/CrossFiltering/Izenda.CrossFiltering';
import CrossFilterSetting from 'modules/CrossFiltering/Izenda.CrossFilterSetting';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import IntegrationStyleAction from 'pages/Izenda.IntegrationStyleAction';
import Config from 'config';
import { isEmpty, flow, get, chunk, compact, head } from 'lodash/fp';
const integrationStyleAction = IntegrationStyleAction.checkDisplayByKey('hideFilter');

const FiltersHeader = connect()(
  ({
    uiStore,
    localization,
    authorization,
    haveFreeze,
    crossFilteringDetails,
    hadSetting,
    removeCrossFiltering,
    removeAllCrossFiltering,
    fieldStore,
    resetDefaultValue,
    enableDeleteAll,
    isReport,
    enableCrossFilteringOption,
    openPopup,
    collapseToggle
  }) => {
    const btnName = haveFreeze && uiStore.doesFreeze ? constants.UNFREEZE_BTN_LABEL : constants.FREEZE_BTN_LABEL;
    const onClickFreeze = function() {
      uiStore.doesFreeze = !uiStore.doesFreeze;
    };

    const showCrossFilteringOption =
      isReport &&
      authorization.checkPrivileges(
        [
          AuthorizationConstants.PERMISSION_KEY.FullReportAndDashboardAccess,
          AuthorizationConstants.PERMISSION_KEY.ReportsFilterPropertiesCrossFiltering
        ],
        AuthorizationConstants.PERMISSION_OPERATOR.Or
      );

    /**
     * Open popup when click cross filter
     */
    const onCrossFilteringOptionClick = (): void => {
      openPopup(CrossFilterSetting, { store: fieldStore });
    };

    /**
     * Check show Cross Filtering Section
     */
    const validateShowCrossFilteringSection = (): boolean => {
      if (isReport) {
        return !isEmpty(crossFilteringDetails);
      } else {
        return !flow(head, get('pillDefinitions'), isEmpty)(crossFilteringDetails);
      }
    };

    const showCrossFiltering = validateShowCrossFilteringSection();

    return (
      <div className="u_table izenda-FilterHeader ">
        <div className="u_tableCell u_alignMiddle" onClick={collapseToggle}>
          <i className="icon-toggle-thick" />
          <strong>{localization.getText('REPORT_LABEL_FILTERS')}</strong>
        </div>

        <div className="u_tableCell u_alignMiddle u_fullWidth izenda-FilterHeader__CrossFilteringContainer">
          {showCrossFiltering && (
            <CrossFiltering
              crossFilteringDetail={crossFilteringDetails}
              showCrossFilteringOption={false}
              hadSetting={false}
              removeCrossFiltering={removeCrossFiltering}
              removeAllCrossFiltering={removeAllCrossFiltering}
              onCrossFilteringOptionClick={onCrossFilteringOptionClick}
              resetDefaultValue={resetDefaultValue}
              enableDeleteAll={enableDeleteAll}
              enableCrossFilteringOption={enableCrossFilteringOption}
            />
          )}
        </div>

        {haveFreeze && (
          <div className="u_table izenda-FilterHeader__headerButton">
            <button className="btn btn-primary btn-freeze u_tableCell" onClick={onClickFreeze}>
              {btnName}
            </button>{' '}
          </div>
        )}
      </div>
    );
  }
);

FiltersHeader.displayName = 'FiltersHeader';

@observer
class ReportViewerFilters extends Component {
  static propTypes = {
    uiStore: PropTypes.object,
    configFields: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    haveFreeze: PropTypes.bool,
    fieldStore: PropTypes.object
  };

  static defaultProps = {
    uiStore: {}
  };

  static displayName = 'ReportViewerFilters';

  render() {
    let {
      uiStore,
      configFields,
      haveFreeze,
      crossFilteringDetails,
      showCrossFilteringOption,
      hadSetting,
      removeCrossFiltering,
      removeAllCrossFiltering,
      resetDefaultValue,
      enableDeleteAll,
      isReport,
      enableCrossFilteringOption,
      accessPriority,
      isLazyLoading,
      afterToggled,
      afterMove
    } = this.props;
    let fieldStore = this.props.fieldStore || {};

    const header = {
      component: FiltersHeader,
      uiStore: uiStore,
      haveFreeze: haveFreeze,
      crossFilteringDetails: crossFilteringDetails,
      showCrossFilteringOption: showCrossFilteringOption,
      hadSetting: hadSetting,
      removeCrossFiltering: removeCrossFiltering,
      removeAllCrossFiltering: removeAllCrossFiltering,
      resetDefaultValue: resetDefaultValue,
      enableDeleteAll: enableDeleteAll,
      isReport: isReport,
      fieldStore: fieldStore,
      enableCrossFilteringOption: enableCrossFilteringOption
    };

    let filterItem = configFields.map((item, index) => {
      const updateCascadingValue = () => {
        return fieldStore.updateCascadingValue && fieldStore.updateCascadingValue.call(fieldStore, item);
      };

      return item.fieldDetails.visible &&
        item.fieldDetails.sourceFieldVisible &&
        item.fieldDetails.sourceFieldFilterable ? (
        <FilterItem
          key={index}
          index={index}
          configField={item}
          updateCascadingValue={updateCascadingValue}
          displayName={get('fieldDetails.alias')}
          viewMode
          accessPriority={accessPriority}
          fieldStore={fieldStore}
          isLazyLoading={isLazyLoading}
        />
      ) : null;
    });

    let chunkFilterItem = chunk(4, compact(filterItem));
    let children = React.Children.count(this.props.children) === 0 ? null : this.props.children;
    const filterExpanded = Config.UIPreferences.ReportFilterSectionExpanded;

    return (
      <Panel
        className="filters-container view-mode-filter"
        header={header}
        collapsible
        initState={filterExpanded}
        afterToggled={afterToggled}
        afterMove={afterMove}
        resizable
        defaultValue={constants.FITLERS_PANEL_UI.minHeight}
        minValue={constants.FITLERS_PANEL_UI.minHeight}
        maxValue={constants.FITLERS_PANEL_UI.maxHeight}
      >
        <ScrollBox>
          <GridLayout className="u_paddingTop">
            {chunkFilterItem.map((item, index) => {
              return (
                <GridLayout.Row key={index} className="u_noMarginAll">
                  {item}
                </GridLayout.Row>
              );
            })}
          </GridLayout>
          {children}
        </ScrollBox>
      </Panel>
    );
  }
}

export default integrationStyleAction(ReportViewerFilters);



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportViewer/Izenda.ReportViewerFilters.js